import { cva } from "class-variance-authority";

/**
 * Badge variants using Deep Trust theme
 * Based on eScribe design system screenshot
 *
 * Status badges use soft backgrounds with colored dot indicators
 * Plan tiers progress: muted → teal soft → teal solid → navy
 * Roles have distinct color associations
 */
export const badgeVariants = cva(
  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        // === PRIMARY VARIANTS ===

        // Default - Solid teal (primary action)
        default: "border-transparent bg-teal-500 text-white",

        // Secondary - Subtle slate outline
        secondary:
          "border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400",

        // Destructive - Solid red
        destructive: "border-transparent bg-red-500 text-white",

        // Outline - Border only
        outline:
          "border-slate-300 bg-transparent text-slate-600 dark:border-slate-600 dark:text-slate-400",

        // === SOFT STATUS VARIANTS (with dot indicators) ===

        // Pending - Soft amber/yellow
        pending:
          "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300",

        // In Progress - Soft blue
        "in-progress":
          "border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-800 dark:bg-sky-950 dark:text-sky-300",

        // Sent - Soft teal
        sent: "border-teal-200 bg-teal-50 text-teal-700 dark:border-teal-800 dark:bg-teal-950 dark:text-teal-300",

        // Completed - Soft teal (same as sent, success state)
        completed:
          "border-teal-200 bg-teal-50 text-teal-700 dark:border-teal-800 dark:bg-teal-950 dark:text-teal-300",

        // Declined - Soft red/pink
        declined:
          "border-red-200 bg-red-50 text-red-600 dark:border-red-800 dark:bg-red-950 dark:text-red-300",

        // Voided - Soft gray
        voided:
          "border-slate-200 bg-slate-100 text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400",

        // Draft - Light gray (most muted)
        draft:
          "border-slate-200 bg-slate-50 text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-500",

        // Expired - Soft red (same as declined)
        expired:
          "border-red-200 bg-red-50 text-red-600 dark:border-red-800 dark:bg-red-950 dark:text-red-300",

        // Viewed - Soft blue (same as in-progress)
        viewed:
          "border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-800 dark:bg-sky-950 dark:text-sky-300",

        // Partially Signed - Soft amber (same as pending)
        "partially-signed":
          "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300",

        // === SEMANTIC VARIANTS (legacy support) ===

        success:
          "border-teal-200 bg-teal-50 text-teal-700 dark:border-teal-800 dark:bg-teal-950 dark:text-teal-300",

        warning:
          "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300",

        danger:
          "border-red-200 bg-red-50 text-red-600 dark:border-red-800 dark:bg-red-950 dark:text-red-300",

        info: "border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-800 dark:bg-sky-950 dark:text-sky-300",

        muted:
          "border-slate-200 bg-slate-50 text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-500",

        // === PLAN TIER VARIANTS ===

        // Free - Muted outline
        "plan-free":
          "border-slate-300 bg-white text-slate-600 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-400",

        // Starter - Soft teal
        "plan-starter":
          "border-teal-200 bg-teal-50 text-teal-700 dark:border-teal-800 dark:bg-teal-950 dark:text-teal-300",

        // Professional - Solid teal
        "plan-professional":
          "border-transparent bg-teal-500 text-white dark:bg-teal-600",

        // Enterprise - Solid navy
        "plan-enterprise":
          "border-transparent bg-slate-900 text-white dark:bg-slate-800",

        // === ROLE VARIANTS ===

        // Signer - Soft amber
        "role-signer":
          "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300",

        // Reviewer - Solid teal
        "role-reviewer":
          "border-transparent bg-teal-500 text-white dark:bg-teal-600",

        // CC - Muted outline
        "role-cc":
          "border-slate-300 bg-white text-slate-600 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-400",

        // Approver - Soft amber with teal accent
        "role-approver":
          "border-teal-300 bg-amber-50 text-teal-700 dark:border-teal-700 dark:bg-amber-950 dark:text-teal-300",

        // Admin - Solid navy
        "role-admin":
          "border-transparent bg-slate-900 text-white dark:bg-slate-800",
      },
      size: {
        sm: "px-2 py-0.5 text-[10px]",
        default: "px-2.5 py-0.5 text-xs",
        lg: "px-3 py-1 text-sm",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

/**
 * Dot color mapping for status badges
 * Returns Tailwind classes for the status indicator dot
 */
export const statusDotColors = {
  // Document status
  pending: "bg-amber-500",
  "in-progress": "bg-sky-500",
  sent: "bg-teal-500",
  completed: "bg-teal-500",
  declined: "bg-red-500",
  voided: "bg-slate-400",
  draft: "bg-slate-300",
  expired: "bg-red-500",
  viewed: "bg-sky-500",
  "partially-signed": "bg-amber-500",
  partially_signed: "bg-amber-500",

  // Semantic (legacy)
  success: "bg-teal-500",
  warning: "bg-amber-500",
  danger: "bg-red-500",
  info: "bg-sky-500",
  muted: "bg-slate-400",
};

/**
 * Get dot color class for a status variant
 */
export const getStatusDotColor = (variant) =>
  statusDotColors[variant] || "bg-slate-400";
